import { RefObject, useEffect } from "react";

/**
 * Hook that handles clicks outside of the passed ref element
 * @param ref - React ref of the element to detect outside clicks
 * @param handler - Function to call when clicking outside
 * @param enabled - Whether the hook is active (default: true)
 */
export function useClickOutside<T extends HTMLElement = HTMLElement>(
  refOrClosest: RefObject<null | T> | string,
  handler: () => void,
  enabled = true
) {
  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (typeof refOrClosest === "string") {
        if (!target.closest(refOrClosest)) {
          handler();
        }
      } else if (
        refOrClosest.current &&
        !refOrClosest.current.contains(target)
      ) {
        handler();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handler();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [refOrClosest, handler, enabled]);
}
